import { FEATURE_FLAGS } from "@repo/shared";
import type { CostOverrideMode, LocalizedString, Translated } from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import {
  useCreatePortfolioCost,
  useUpdatePortfolioCost,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { AdvisorOverrideSelect } from "./advisor-override-select";
import { T } from "./portfolio-cost-dialog.i18n";

type BracketInput = {
  id: string;
  startValue: string;
  value: string;
};

type PortfolioCostDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioCollectionId: string;
  portfolioCost: any | null;
};

// Upper bound for bracket start values and amount-type values. Mirrors MAX_AMOUNT
// in the backend validateBrackets (portfolio-collection-helpers.ts) — kept in sync
// manually (intentionally not shared). Stays well under the numeric(18,4) column
// ceiling (~1e14). Percentage values are capped at 100 separately.
const MAX_AMOUNT = 1_000_000_000_000; // 1e12
const MAX_PERCENTAGE = 100;

/**
 * Determines which cost type options are available based on category
 * API supports:
 * - deposit_cost_pct_brackets (DEPOSIT + pct)
 * - recurring_cost_pct_brackets, recurring_cost_amount_brackets (RECURRING)
 * Note: WITHDRAWAL costs are not supported in the deposits array
 */
function getCostTypeOptions(category: "RECURRING" | "DEPOSIT", t: Translated<typeof T>) {
  switch (category) {
    case "DEPOSIT":
      // Only percentage allowed for deposits
      return [{ value: "pct", label: t.percentage }];
    case "RECURRING":
      // Both percentage and amount allowed
      return [
        { value: "pct", label: t.percentage },
        { value: "amount", label: t.amount },
      ];
    default:
      return [{ value: "pct", label: t.percentage }];
  }
}

export function PortfolioCostDialog({
  open,
  onOpenChange,
  portfolioCollectionId,
  portfolioCost,
}: PortfolioCostDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const CATEGORY_LABELS: Record<string, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
  };

  const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    RECURRING: t.categoryRecurringDesc,
    DEPOSIT: t.categoryDepositDesc,
  };

  const createPCost = useCreatePortfolioCost();
  const updatePCost = useUpdatePortfolioCost();
  const isPending = createPCost.isPending || updatePCost.isPending;
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [costType, setCostType] = useState<string>("pct");
  const [category, setCategory] = useState<"RECURRING" | "DEPOSIT">("RECURRING");
  const [brackets, setBrackets] = useState<BracketInput[]>([
    { id: crypto.randomUUID(), startValue: "0", value: "" },
  ]);
  const [error, setError] = useState<string | null>(null);
  // QL-20 — shown and sent only while the tenant's cost_overrides feature is on.
  const { enabled: overridesEnabled } = useFeatureFlag(FEATURE_FLAGS.COST_OVERRIDES);
  const [advisorOverride, setAdvisorOverride] = useState<CostOverrideMode>("off");

  useEffect(() => {
    if (open) setAdvisorOverride(portfolioCost?.advisorOverride ?? "off");
  }, [open, portfolioCost]);

  const isEditing = portfolioCost !== null;
  const editingPortfolioCostName = portfolioCost ? localize(portfolioCost.nameI18n) : "";
  const costTypeOptions = getCostTypeOptions(category, t);
  // All supported categories (RECURRING, DEPOSIT) use brackets
  const useBrackets = true;

  // Reset costType when category changes and current costType is not available
  useEffect(() => {
    const options = getCostTypeOptions(category, t);
    if (!options.find((opt) => opt.value === costType)) {
      setCostType(options[0].value);
    }
  }, [category, costType, t]);

  useEffect(() => {
    if (open) {
      if (portfolioCost) {
        // Get the value for user's language from nameI18n, with fallback to any available value
        const i18n = portfolioCost.nameI18n || {};
        const nameInUserLang = localize(i18n);

        // Other translations (excluding user's language) go in nameI18n for the dialog
        const otherTranslations = Object.fromEntries(
          Object.entries(i18n).filter(([lang]) => lang !== userLanguage),
        );

        setName(nameInUserLang);
        setNameI18n(otherTranslations);
        setCostType(portfolioCost.costType);
        setCategory(portfolioCost.category as "RECURRING" | "DEPOSIT");

        // Always use brackets (all supported categories use brackets)
        setBrackets(
          portfolioCost.brackets.map((b: any) => ({
            id: crypto.randomUUID(),
            startValue: b.startValue.toString(),
            value: b.value.toString(),
          })),
        );
      } else {
        setName("");
        setNameI18n({});
        setCostType("pct");
        setCategory("RECURRING");
        setBrackets([{ id: crypto.randomUUID(), startValue: "0", value: "" }]);
      }
      setError(null);
    }
  }, [open, portfolioCost, userLanguage, localize]);

  const handleAddBracket = () => {
    if (brackets.length >= 10) return;
    setBrackets([...brackets, { id: crypto.randomUUID(), startValue: "", value: "" }]);
  };

  const handleRemoveBracket = (id: string) => {
    const index = brackets.findIndex((b) => b.id === id);
    if (index === 0 || brackets.length <= 1) return;
    setBrackets(brackets.filter((b) => b.id !== id));
  };

  const handleBracketChange = (id: string, field: keyof BracketInput, value: string) => {
    setBrackets(brackets.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    // Validate and parse brackets
    const parsedBrackets: { startValue: number; value: number }[] = [];
    for (let i = 0; i < brackets.length; i++) {
      const startValue = parseFloat(brackets[i].startValue);
      const value = parseFloat(brackets[i].value);

      if (Number.isNaN(startValue)) {
        setError(`${t.bracket} ${i + 1}: ${t.invalidStartValue}`);
        return;
      }

      if (Number.isNaN(value)) {
        setError(`${t.bracket} ${i + 1}: ${t.invalidValue}`);
        return;
      }

      if (startValue < 0) {
        setError(`${t.bracket} ${i + 1}: ${t.startValueNegative}`);
        return;
      }

      if (value < 0) {
        setError(`${t.bracket} ${i + 1}: ${t.valueNegative}`);
        return;
      }

      // Upper bounds — type-aware: percentage costs cap at 100, amounts at MAX_AMOUNT.
      if (startValue > MAX_AMOUNT) {
        setError(`${t.bracket} ${i + 1}: ${t.startValueTooLarge}`);
        return;
      }

      const isPct = costType === "pct";
      if (isPct && value > MAX_PERCENTAGE) {
        setError(`${t.bracket} ${i + 1}: ${t.valueExceedsPct}`);
        return;
      }
      if (!isPct && value > MAX_AMOUNT) {
        setError(`${t.bracket} ${i + 1}: ${t.valueExceedsAmount}`);
        return;
      }

      // Check ascending order
      if (i > 0 && startValue <= parsedBrackets[i - 1].startValue) {
        setError(`${t.bracket} ${i + 1}: ${t.startValueMustBeGreater}`);
        return;
      }

      parsedBrackets.push({ startValue, value });
    }

    if (parsedBrackets[0].startValue !== 0) {
      setError(t.firstBracketMustStartAtZero);
      return;
    }

    // Build the complete nameI18n with user's language value
    const completeNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name.trim(),
    };

    const submitData = {
      nameI18n: completeNameI18n,
      costType,
      useBrackets,
      category,
      brackets: parsedBrackets,
      ...(overridesEnabled && { advisorOverride }),
    };

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => setError(err.message || t.failedToSave),
    };

    if (isEditing) {
      updatePCost.mutate(
        { collectionId: portfolioCollectionId, pcId: portfolioCost.id, data: submitData },
        callbacks,
      );
    } else {
      createPCost.mutate({ collectionId: portfolioCollectionId, data: submitData }, callbacks);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="max-w-lg">
        <DialogScrollHeader>
          <DialogTitle>
            {isEditing
              ? editingPortfolioCostName
                ? `${t.editPortfolioCost} (${editingPortfolioCostName})`
                : t.editPortfolioCost
              : t.addPortfolioCost}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-6">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <LocalizedInput
              id="name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={(value) => setName(value)}
              onI18nChange={(i18nValue) => setNameI18n(i18nValue)}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
              disabled={isPending}
            />

            <div className="space-y-2">
              <Label htmlFor="category">{t.whenToApply}</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as "RECURRING" | "DEPOSIT")}
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{CATEGORY_DESCRIPTIONS[category]}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="costType">{t.valueType}</Label>
              <Select
                value={costType}
                onValueChange={setCostType}
                disabled={isPending || costTypeOptions.length === 1}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {costTypeOptions.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {costType === "pct" ? t.percentageDesc : t.amountDesc}
              </p>
            </div>

            {/* Brackets Input */}
            <div className="space-y-3">
              <Label>
                {t.brackets} <span className="text-destructive">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">{t.bracketsDesc}</p>

              <div className="space-y-2">
                {/* Header row */}
                <div className="grid grid-cols-[1fr_1fr_40px] gap-2 text-xs font-medium text-muted-foreground">
                  <div>{t.fromAccountValue}</div>
                  <div>{costType === "pct" ? t.feePct : t.feeAmount}</div>
                  <div></div>
                </div>

                {/* Bracket rows */}
                {brackets.map((bracket, index) => (
                  <div
                    key={bracket.id}
                    className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center"
                  >
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={bracket.startValue}
                      onChange={(e) =>
                        handleBracketChange(bracket.id, "startValue", e.target.value)
                      }
                      disabled={isPending || index === 0}
                      placeholder="0"
                      className={index === 0 ? "bg-muted" : ""}
                    />
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={bracket.value}
                      onChange={(e) => handleBracketChange(bracket.id, "value", e.target.value)}
                      disabled={isPending}
                      placeholder={costType === "pct" ? "1.00" : "1000"}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tooltip={t.delete}
                      className="h-8 w-8 p-0"
                      onClick={() => handleRemoveBracket(bracket.id)}
                      disabled={isPending || index === 0 || brackets.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddBracket}
                disabled={isPending || brackets.length >= 10}
                className="w-full"
              >
                {t.addBracket} {brackets.length >= 10 && t.maxBrackets}
              </Button>
            </div>
            {overridesEnabled && (
              <AdvisorOverrideSelect
                id="portfolio-cost-advisor-override"
                value={advisorOverride}
                onChange={setAdvisorOverride}
                disabled={isPending}
              />
            )}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {isEditing ? t.save : t.addPortfolioCost}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
